import { useEffect, useId, useState } from 'react';
import type { FormEvent } from 'react';

import { useAiProviders } from '@/features/ai-providers';
import type { AiProvider } from '@/features/ai-providers';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label, RequiredMark } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetBody,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { useDiscardGuard } from '@/shared/hooks/useDiscardGuard';

import { EFFORT_LABELS, EFFORT_OPTIONS } from '../effortLabels';
import { isLlmProfileError } from '../errors';
import type { LlmProfileErrorField } from '../errors';
import type { LlmProfile, LlmProfileEffort } from '../types';
import { validateModel, validateName } from '../validation';
import { ClaudeModelCombobox } from './ClaudeModelCombobox';
import { NoAiProvidersEmptyState } from './NoAiProvidersEmptyState';

export type LlmProfileFormMode =
  { kind: 'create' } | { kind: 'edit'; entry: LlmProfile };

/** Suffixed onto a non-selectable provider's option label (spec 136 FR-006). */
const STATUS_LABEL: Record<AiProvider['connection_status'], string> = {
  untested: 'Untested',
  failed: 'Failed',
  ok: 'Connected',
};

export interface LlmProfileFormValues {
  name: string;
  model: string;
  effort: LlmProfileEffort;
  max_turns: number | null;
  description: string | null;
  ai_provider_id: string;
}

/**
 * Add a profile, or edit an existing one.
 *
 * Name comes first, then model, effort, max turns, and a required AI
 * Provider selector — no base_url, credential, or workspace field anywhere
 * (FR-012), and no test-connection action (FR-014). When no AI Provider
 * exists yet, submission is blocked and `NoAiProvidersEmptyState` points
 * the admin at the AI Providers section instead (FR-013).
 *
 * Mirrors `LlmProfileFormSheet`'s (pre-refactor) load-bearing decisions:
 * remount per target, dismissal blocked while a save is in flight, a failed
 * submit keeps the sheet open with the input intact.
 */
export function LlmProfileFormSheet({
  mode,
  open,
  isPending,
  error,
  onSubmit,
  onClearError,
  onCancel,
}: {
  mode: LlmProfileFormMode;
  open: boolean;
  isPending: boolean;
  error: unknown;
  onSubmit: (input: LlmProfileFormValues) => void;
  onClearError: () => void;
  onCancel: () => void;
}) {
  const editing = mode.kind === 'edit';
  const entry = editing ? mode.entry : undefined;

  const { entries: providers, isPending: providersPending } = useAiProviders();
  const availableProviders = providers ?? [];
  // A profile has no credentials of its own — only `ai_provider_id` — so a
  // provider whose connection is unverified or broken fails silently until a
  // workflow execution runs it (spec 136). The profile's own already-saved
  // provider is the one exception: unrelated edits must not be blocked just
  // because that provider's connection degraded after the profile was set up.
  const isProviderSelectable = (provider: AiProvider) =>
    provider.connection_status === 'ok' ||
    provider.id === entry?.ai_provider_id;
  const noProvidersAvailable =
    !providersPending &&
    availableProviders.filter(isProviderSelectable).length === 0;

  const [model, setModel] = useState(entry?.model ?? '');
  const [name, setName] = useState(entry?.name ?? '');
  const [effort, setEffort] = useState<LlmProfileEffort>(
    entry?.effort ?? EFFORT_OPTIONS[0],
  );
  const [maxTurns, setMaxTurns] = useState(
    entry?.max_turns != null ? String(entry.max_turns) : '',
  );
  const [description, setDescription] = useState(entry?.description ?? '');
  const [aiProviderId, setAiProviderId] = useState<string>(
    entry?.ai_provider_id ??
      availableProviders.find((provider) => provider.connection_status === 'ok')
        ?.id ??
      '',
  );
  const [submitted, setSubmitted] = useState(false);
  // Seeding the provider selector is the form's doing, not the admin's; only
  // a choice they make counts as a change.
  const [providerChosen, setProviderChosen] = useState(false);

  // `useAiProviders()` resolves after this component's initial render — the
  // state initializer above only ever sees an empty list on mount. Once the
  // real list arrives, seed the still-unset selector with its first verified
  // entry (never overriding a value the admin, or an edited entry, already
  // has) — never an unverified/failed one, matching the Select's own gate
  // (spec 136 FR-001/FR-002).
  useEffect(() => {
    if (aiProviderId !== '') return;
    const firstOkProviderId = availableProviders.find(
      (provider) => provider.connection_status === 'ok',
    )?.id;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- seeding a controlled selector once its async data source resolves, not deriving render state
    if (firstOkProviderId) setAiProviderId(firstOkProviderId);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only react to the list arriving, not to aiProviderId being cleared by user choice
  }, [availableProviders]);

  const modelId = useId();
  const modelErrorId = useId();
  const nameId = useId();
  const nameErrorId = useId();
  const effortId = useId();
  const effortErrorId = useId();
  const maxTurnsId = useId();
  const maxTurnsErrorId = useId();
  const descriptionId = useId();
  const descriptionHelpId = useId();
  const descriptionErrorId = useId();
  const providerId = useId();
  const providerErrorId = useId();

  const serverError = isLlmProfileError(error) ? error : undefined;
  const genericError =
    error && !serverError ? 'The request failed. Please try again.' : undefined;

  const localNameError = validateName(name);
  const localModelError = validateModel(model);

  const trimmedMaxTurns = maxTurns.trim();
  const parsedMaxTurns =
    trimmedMaxTurns === '' ? null : Number.parseInt(trimmedMaxTurns, 10);
  const maxTurnsError =
    trimmedMaxTurns !== '' &&
    (parsedMaxTurns === null ||
      Number.isNaN(parsedMaxTurns) ||
      parsedMaxTurns < 1 ||
      String(parsedMaxTurns) !== trimmedMaxTurns)
      ? 'Enter a whole number of at least 1, or leave it blank.'
      : null;

  function messageFor(field: LlmProfileErrorField): string | undefined {
    if (serverError?.field === field) return serverError.message;
    if (field === 'form') {
      if (serverError?.field === 'slug') return serverError.message;
      return genericError;
    }
    if (field === 'name')
      return submitted ? (localNameError ?? undefined) : undefined;
    if (field === 'model')
      return submitted ? (localModelError ?? undefined) : undefined;
    if (field === 'max_turns') return maxTurnsError ?? undefined;
    return undefined;
  }

  /** Structural emptiness, the unparsed max-turns state, and a chosen AI Provider (FR-013). */
  const canSubmit =
    name.trim().length > 0 &&
    model.trim().length > 0 &&
    maxTurnsError === null &&
    aiProviderId !== '' &&
    !noProvidersAvailable &&
    !isPending;

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setSubmitted(true);
    if (!canSubmit) return;
    onSubmit({
      name,
      model,
      effort,
      max_turns: parsedMaxTurns,
      // Blank is none (spec 168); the service trims and checks the rest.
      description: description.trim() === '' ? null : description.trim(),
      ai_provider_id: aiProviderId,
    });
  }

  const dirty =
    name !== (entry?.name ?? '') ||
    model !== (entry?.model ?? '') ||
    effort !== (entry?.effort ?? EFFORT_OPTIONS[0]) ||
    maxTurns !== (entry?.max_turns != null ? String(entry.max_turns) : '') ||
    description !== (entry?.description ?? '') ||
    (editing ? aiProviderId !== entry?.ai_provider_id : providerChosen);
  // A successful save closes through `open`, so it never asks.
  const { requestClose, dialog } = useDiscardGuard(dirty, onCancel);

  const modelMessage = messageFor('model');
  const nameMessage = messageFor('name');
  const effortMessage = messageFor('effort');
  const maxTurnsMessage = messageFor('max_turns');
  const descriptionMessage = messageFor('description');
  const providerMessage = messageFor('ai_provider_id');
  const formMessage = messageFor('form');

  return (
    <>
      <Sheet
        open={open}
        onOpenChange={(next) => {
          if (isPending) return;
          if (!next) requestClose();
        }}
      >
        <SheetContent className="sm:max-w-lg">
          <SheetHeader>
            <SheetTitle>
              {editing ? `Edit ${entry?.name}` : 'Add a profile'}
            </SheetTitle>
            <SheetDescription>
              A profile names a model and the settings a workflow step runs it
              under.
            </SheetDescription>
          </SheetHeader>

          <form
            onSubmit={handleSubmit}
            className="flex min-h-0 flex-1 flex-col"
          >
            <SheetBody className="flex flex-col gap-4">
              <div className="flex flex-col gap-1.5">
                <div className="flex items-center gap-0.5">
                  <Label htmlFor={nameId}>Name</Label>
                  <RequiredMark />
                </div>
                <Input
                  id={nameId}
                  value={name}
                  onChange={(event) => {
                    setName(event.target.value);
                    if (serverError?.field === 'name') onClearError();
                  }}
                  disabled={isPending}
                  placeholder="Implementation model"
                  autoFocus
                  aria-invalid={nameMessage ? true : undefined}
                  aria-describedby={nameMessage ? nameErrorId : undefined}
                />
                {nameMessage ? (
                  <p id={nameErrorId} className="text-destructive text-xs">
                    {nameMessage}
                  </p>
                ) : null}
              </div>

              <div className="flex flex-col gap-1.5">
                <div className="flex items-center gap-0.5">
                  <Label htmlFor={modelId}>Model</Label>
                  <RequiredMark />
                </div>
                <ClaudeModelCombobox
                  id={modelId}
                  value={model}
                  onChange={(next) => {
                    setModel(next);
                    if (serverError?.field === 'model') onClearError();
                  }}
                  disabled={isPending}
                  aria-invalid={modelMessage ? true : undefined}
                  aria-describedby={modelMessage ? modelErrorId : undefined}
                />
                {modelMessage ? (
                  <p id={modelErrorId} className="text-destructive text-xs">
                    {modelMessage}
                  </p>
                ) : null}
              </div>

              <div className="flex flex-col gap-1.5">
                <div className="flex items-center gap-0.5">
                  <Label htmlFor={effortId}>Effort</Label>
                  <RequiredMark />
                </div>
                <Select
                  value={effort}
                  onValueChange={(next) => {
                    if (!next) return;
                    setEffort(next as LlmProfileEffort);
                    if (serverError?.field === 'effort') onClearError();
                  }}
                  disabled={isPending}
                >
                  <SelectTrigger
                    id={effortId}
                    aria-label="Effort"
                    aria-invalid={effortMessage ? true : undefined}
                    aria-describedby={effortMessage ? effortErrorId : undefined}
                  >
                    <SelectValue>
                      {(value: string) =>
                        EFFORT_LABELS[value as LlmProfileEffort]
                      }
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {EFFORT_OPTIONS.map((option) => (
                      <SelectItem key={option} value={option}>
                        {EFFORT_LABELS[option]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {effortMessage ? (
                  <p id={effortErrorId} className="text-destructive text-xs">
                    {effortMessage}
                  </p>
                ) : null}
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor={maxTurnsId}>
                  Max turns{' '}
                  <span className="text-muted-foreground font-normal">
                    (optional)
                  </span>
                </Label>
                <Input
                  id={maxTurnsId}
                  type="number"
                  min={1}
                  step={1}
                  inputMode="numeric"
                  value={maxTurns}
                  onChange={(event) => {
                    setMaxTurns(event.target.value);
                    if (serverError?.field === 'max_turns') onClearError();
                  }}
                  disabled={isPending}
                  placeholder="No override"
                  aria-invalid={maxTurnsMessage ? true : undefined}
                  aria-describedby={
                    maxTurnsMessage ? maxTurnsErrorId : undefined
                  }
                />
                {maxTurnsMessage ? (
                  <p id={maxTurnsErrorId} className="text-destructive text-xs">
                    {maxTurnsMessage}
                  </p>
                ) : null}
              </div>

              {/* What the profile is for, in the admin's words: everyone who
                  chooses or reads a step sees it (spec 214). */}
              <div className="flex flex-col gap-1.5">
                <Label htmlFor={descriptionId}>
                  Description{' '}
                  <span className="text-muted-foreground font-normal">
                    (optional)
                  </span>
                </Label>
                <Input
                  id={descriptionId}
                  value={description}
                  maxLength={200}
                  onChange={(event) => {
                    setDescription(event.target.value);
                    if (serverError?.field === 'description') onClearError();
                  }}
                  disabled={isPending}
                  placeholder="What this profile is for"
                  aria-invalid={descriptionMessage ? true : undefined}
                  aria-describedby={
                    descriptionMessage
                      ? `${descriptionHelpId} ${descriptionErrorId}`
                      : descriptionHelpId
                  }
                />
                <p
                  id={descriptionHelpId}
                  className="text-muted-foreground text-xs"
                >
                  One line, up to 200 characters. Shown to everyone who chooses
                  this profile.
                </p>
                {descriptionMessage ? (
                  <p
                    id={descriptionErrorId}
                    className="text-destructive text-xs"
                  >
                    {descriptionMessage}
                  </p>
                ) : null}
              </div>

              <div className="flex flex-col gap-1.5">
                <div className="flex items-center gap-0.5">
                  <Label htmlFor={providerId}>AI Provider</Label>
                  <RequiredMark />
                </div>
                {noProvidersAvailable ? (
                  <NoAiProvidersEmptyState />
                ) : (
                  <Select
                    value={aiProviderId}
                    onValueChange={(next) => {
                      if (!next) return;
                      setAiProviderId(next);
                      setProviderChosen(true);
                      if (serverError?.field === 'ai_provider_id')
                        onClearError();
                    }}
                    disabled={isPending || providersPending}
                  >
                    <SelectTrigger
                      id={providerId}
                      aria-label="AI Provider"
                      aria-invalid={providerMessage ? true : undefined}
                      aria-describedby={
                        providerMessage ? providerErrorId : undefined
                      }
                    >
                      <SelectValue>
                        {(value: string) =>
                          availableProviders.find(
                            (provider) => provider.id === value,
                          )?.name ?? 'Choose an AI Provider'
                        }
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {availableProviders.map((providerOption) => {
                        const selectable = isProviderSelectable(providerOption);
                        return (
                          <SelectItem
                            key={providerOption.id}
                            value={providerOption.id}
                            disabled={!selectable}
                          >
                            {selectable
                              ? providerOption.name
                              : `${providerOption.name} — ${STATUS_LABEL[providerOption.connection_status]}`}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                )}
                {providerMessage ? (
                  <p id={providerErrorId} className="text-destructive text-xs">
                    {providerMessage}
                  </p>
                ) : null}
              </div>

              {formMessage ? (
                <p role="alert" className="text-destructive text-sm">
                  {formMessage}
                </p>
              ) : null}
            </SheetBody>

            <SheetFooter>
              <SheetClose
                render={<Button type="button" variant="outline" />}
                disabled={isPending}
              >
                Cancel
              </SheetClose>
              <Button type="submit" disabled={!canSubmit}>
                {isPending ? 'Saving…' : editing ? 'Save' : 'Add'}
              </Button>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>
      {dialog}
    </>
  );
}
